import type { BillStatus, Deal, DealStage, PurchaseRequestStatus } from './models';

// Deal stages
export const DEAL_STAGES = {
  LEAD: 'lead',
  QUALIFIED: 'qualified',
  PROPOSAL: 'proposal',
  NEGOTIATION: 'negotiation',
  WON: 'won',
  LOST: 'lost',
} as const;

export const DEAL_STAGE_LABELS: Record<string, string> = {
  lead: 'לקוח פוטנציאלי',
  qualified: 'איכותי',
  proposal: 'הצעת מחיר',
  negotiation: 'משא ומתן',
  won: 'נסגר',
  lost: 'אבד',
};

/**
 * Early-stage ("potential client") CRM deals that aren't yet worth quoting:
 * לקוח פוטנציאלי (lead) + איכותי (qualified). The quotes app hides these from its deal
 * lists, pipeline and pickers — lead management lives in the CRM app.
 */
export function isEarlyStageDeal(stage: DealStage): boolean {
  return stage === DEAL_STAGES.LEAD || stage === DEAL_STAGES.QUALIFIED;
}

/** A deal advanced enough to quote / attach a quote or bill to (past lead/qualified). */
export function isQuotableDeal(deal: Pick<Deal, 'stage'>): boolean {
  return !isEarlyStageDeal(deal.stage);
}

// Quote statuses
export const QUOTE_STATUSES = {
  DRAFT: 'draft',
  SENT: 'sent',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  EXPIRED: 'expired',
} as const;

export const QUOTE_STATUS_LABELS: Record<string, string> = {
  draft: 'טיוטה',
  finalized: 'סופי',
  sent: 'נשלח',
  accepted: 'התקבל',
  rejected: 'נדחה',
  expired: 'פג תוקף',
};

// Bill statuses
export const BILL_STATUS_LABELS: Record<string, string> = {
  draft: 'טיוטה',
  issued: 'הונפק',
  'partially-paid': 'שולם חלקית',
  paid: 'שולם',
  cancelled: 'בוטל',
};

/**
 * Bill status -> `<Badge variant>` (@elka/ui), so every bill table shows the same colour.
 * The variant union is spelled out rather than imported as BadgeProps['variant']: @elka/ui
 * already imports @elka/types, and importing back would close the cycle.
 */
export const BILL_STATUS_VARIANTS: Record<
  BillStatus,
  'default' | 'success' | 'warning' | 'danger' | 'info' | 'brand'
> = {
  draft: 'default',
  issued: 'info',
  'partially-paid': 'warning',
  paid: 'success',
  cancelled: 'danger',
};

// Quote types
export const QUOTE_TYPE_LABELS: Record<string, string> = {
  'boq-no-prices': 'כתב כמויות ללא מחירים',
  'boq-with-prices': 'כתב כמויות עם מחירים',
  'no-boq': 'ללא כתב כמויות',
  mashkal: 'משכ"ל',
};

// Cost estimate statuses
export const COST_ESTIMATE_STATUS_LABELS: Record<string, string> = {
  draft: 'טיוטה',
  'bid-sent': 'נשלח לספקים',
  priced: 'תומחר',
  quoted: 'נוצרה הצעה',
};

// Deal quoting status (stored on Deal document)
export type DealQuotingStatus =
  | 'draft'
  | 'boq-intake'
  | 'estimating'
  | 'supplier-pricing'
  | 'ready-to-quote'
  | 'quoting'
  | 'quoted'
  | 'accepted';

export const DEAL_QUOTING_STATUS_LABELS: Record<DealQuotingStatus, string> = {
  draft: 'טיוטא',
  'boq-intake': 'קליטת כ"כ',
  estimating: 'בתמחור',
  'supplier-pricing': 'בקשת מחיר מספקים',
  'ready-to-quote': 'מוכן להצעה',
  quoting: 'הכנת הצעה',
  quoted: 'הצעה נשלחה',
  accepted: 'הצעה התקבלה',
};

// Supplier bid request statuses
export const BID_REQUEST_STATUSES = {
  DRAFT: 'draft',
  SENT: 'sent',
  COMPARING: 'comparing',
  CONVERTED: 'converted',
} as const;

export const BID_REQUEST_STATUS_LABELS: Record<string, string> = {
  draft: 'טיוטה',
  sent: 'נשלח',
  comparing: 'השוואת מחירים',
  converted: 'הומר להזמנה',
};

// Purchase request statuses (worker-submitted materials requests)
export const PURCHASE_REQUEST_STATUS_LABELS: Record<PurchaseRequestStatus, string> = {
  open: 'פתוח',
  in_progress: 'בטיפול',
  closed: 'סגור',
};

// Purchase requests only ever need "unit" or "meter" — a deliberately narrow
// slice of the full UNITS list below.
export const PURCHASE_REQUEST_UNITS = ["יח'", "מ'"] as const;

// The one canonical units list (order is the dropdown order). מ"ק (now קוב), ק"ג, שרות and תא
// are not offered; rows saved with a legacy spelling (קומפ', מ"ק, ...) still show it — see
// isListedUnit. Functions can't import this: functions/core/src/types.ts mirrors it as string.
export const UNITS = [
  "יח'", // Unit
  "מ'", // Meter (length)
  'קומפלט', // Complete
  'שעה', // Hour
  'יום', // Day
  'מ"ר', // Square meter
  'קוב', // Cubic meter
  "נק'", // Point
  'חודש', // Month
] as const;

export type Unit = (typeof UNITS)[number];

/** BoQ rows: a note is an isHeader row with this unit. */
export const NOTE_UNIT = 'הערה';
/** BoQ unit-dropdown value that turns a row into a chapter header (stored as unit ''). */
export const CHAPTER_UNIT = '__chapter__';

/** BoQ / bill dropdown: UNITS with the note and chapter pseudo-units after קומפלט. */
export const BOQ_UNITS: readonly string[] = [
  ...UNITS.slice(0, 3),
  NOTE_UNIT,
  CHAPTER_UNIT,
  ...UNITS.slice(3),
];

/** False for a unit no longer offered (e.g. an old row's שרות) — the dropdown adds it back. */
export const isListedUnit = (unit: string): boolean => (UNITS as readonly string[]).includes(unit);

// VAT rate (Israel)
export const VAT_RATE = 0.18; // 18%

// Activity types
export const ACTIVITY_TYPES = {
  CALL: 'call',
  EMAIL: 'email',
  MEETING: 'meeting',
  NOTE: 'note',
} as const;

export const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  call: 'שיחה',
  email: 'אימייל',
  meeting: 'פגישה',
  note: 'הערה',
  task: 'משימה',
};

// Call outcomes
export const CALL_OUTCOMES = {
  ANSWERED: 'answered',
  NO_ANSWER: 'no_answer',
  BUSY: 'busy',
  VOICEMAIL: 'voicemail',
  CALLBACK_SCHEDULED: 'callback_scheduled',
} as const;

export const CALL_OUTCOME_LABELS: Record<string, string> = {
  answered: 'נענה',
  no_answer: 'לא נענה',
  busy: 'תפוס',
  voicemail: 'תא קולי',
  callback_scheduled: 'תואם חזרה',
};

// Call directions
export const CALL_DIRECTIONS = {
  INBOUND: 'inbound',
  OUTBOUND: 'outbound',
} as const;

export const CALL_DIRECTION_LABELS: Record<string, string> = {
  inbound: 'נכנסת',
  outbound: 'יוצאת',
};

// Call connection — step 1 of the two-level outcome (did we reach the contact)
export const CALL_CONNECTIONS = {
  ANSWERED: 'answered',
  NO_ANSWER: 'no_answer',
  VOICEMAIL: 'voicemail',
  BUSY: 'busy',
} as const;

export const CALL_CONNECTION_LABELS: Record<string, string> = {
  answered: 'נענה',
  no_answer: 'לא נענה',
  voicemail: 'תא קולי',
  busy: 'תפוס',
};

// Call result — step 2 of the two-level outcome (what happened; only when answered)
export const CALL_RESULTS = {
  INTERESTED: 'interested',
  NEEDS_QUOTE: 'needs_quote',
  SCHEDULE_MEETING: 'schedule_meeting',
  NOT_RELEVANT: 'not_relevant',
  CLOSED_WON: 'closed_won',
} as const;

export const CALL_RESULT_LABELS: Record<string, string> = {
  interested: 'מעוניין',
  needs_quote: 'צריך הצעת מחיר',
  schedule_meeting: 'לתאם פגישה',
  not_relevant: 'לא רלוונטי כרגע',
  closed_won: 'נסגר/הוזמן',
};

// Task statuses
export const TASK_STATUSES = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
} as const;

export const TASK_STATUS_LABELS: Record<string, string> = {
  pending: 'ממתין',
  in_progress: 'בביצוע',
  completed: 'הושלם',
};

// Task priorities
export const TASK_PRIORITIES = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
} as const;

export const TASK_PRIORITY_LABELS: Record<string, string> = {
  low: 'נמוך',
  medium: 'בינוני',
  high: 'גבוה',
};
